import { useEffect, useState } from 'react'

import {
  LIVEKIT_VIDEO_TOGGLE_BUSY_EVENT,
  readVideoToggleBusy,
} from '../services/liveKitVideoBackground'

/**
 * True while the background-segmentation pipeline is being torn down and rebuilt.
 *
 * Used to stop turning the camera on (never off) for that window. Every camera restart makes
 * the processor library `destroy()` and re-`init()`, and each `init()` allocates
 * a **new WebGL context**; toggling faster than the browser reclaims them pushes
 * the page past its context ceiling, at which point the browser evicts the
 * least-recently-used context — often the Phaser game canvas, which is blank
 * until the browser restores the context, if it does.
 *
 * Reads the state from a window event rather than a prop so the toolbar does not
 * need a new field on the shared video-control surface. Seeded from the last
 * published state: a button that mounts during the cooldown (the
 * picture-in-picture window, the call bar) would otherwise offer "on" until the
 * cooldown ends, and the connector drops that click.
 */
export function useVideoToggleBusy(): boolean {
  const [busy, setBusy] = useState(() => readVideoToggleBusy())

  useEffect(() => {
    const handleBusy = (event: CustomEvent<{ busy: boolean }>) => {
      setBusy(Boolean(event.detail?.busy))
    }
    window.addEventListener(LIVEKIT_VIDEO_TOGGLE_BUSY_EVENT, handleBusy as EventListener)
    // Anything published between the first render and this listener.
    setBusy(readVideoToggleBusy())
    return () => {
      window.removeEventListener(LIVEKIT_VIDEO_TOGGLE_BUSY_EVENT, handleBusy as EventListener)
    }
  }, [])

  return busy
}

export default useVideoToggleBusy
